import ParanoidiAmbientBackground from "../../components/ParanoidiAmbientBackground";
import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  FaFacebookF,
  FaWhatsapp,
  FaInstagram,
  FaSpotify,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import SongFinderSection from "../../paranoidi/sections/SongFinderSection";
import LanguageSwitcher from "../../shared/components/LanguageSwitcher";

function formatArtistNameFromSlug(slug, demoLabel = "Demo-esiintyjä") {
  if (!slug || slug === "demo") {
    return demoLabel;
  }

  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function resolvePublicAssetUrl(url) {
  const rawUrl = String(url || "").trim();

  if (!rawUrl) {
    return "";
  }

  if (
    rawUrl.startsWith("http://") ||
    rawUrl.startsWith("https://") ||
    rawUrl.startsWith("data:") ||
    rawUrl.startsWith("blob:")
  ) {
    return rawUrl;
  }

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (isLocalhost && rawUrl.startsWith("/backend/")) {
    return `http://localhost:4000${rawUrl}`;
  }

  if (isLocalhost && rawUrl.startsWith("/uploads/")) {
    return `http://localhost:4000${rawUrl}`;
  }

  return rawUrl;
}

function normalizeRequestSponsors(value) {
  const items = Array.isArray(value) ? value : [];

  return items
    .slice(0, 5)
    .map((item) => ({
      id: String(item?.id || item?.name || "").trim(),
      name: String(item?.name || "").trim(),
      offerText: String(
        item?.offerText || item?.offer_text || item?.description || ""
      ).trim(),
      logoUrl: String(item?.logoUrl || item?.logo_url || item?.logo || "").trim(),
      linkUrl: String(item?.linkUrl || item?.link_url || item?.href || "").trim(),
      active: item?.active !== false,
    }))
    .filter(
      (item) => item.active && (item.name || item.logoUrl || item.offerText)
    );
}

function normalizeFaqItems(value) {
  const items = Array.isArray(value) ? value : [];

  return items
    .slice(0, 8)
    .map((item) => ({
      id: String(item?.id || item?.question || "").trim(),
      question: String(item?.question || item?.title || item?.q || "")
        .trim()
        .slice(0, 160),
      answer: String(item?.answer || item?.text || item?.a || "")
        .trim()
        .slice(0, 900),
      active: item?.active !== false,
    }))
    .filter((item) => item.active && item.question && item.answer);
}

function normalizeSettings(settings) {
  return {
    isLiveActive: Boolean(settings?.isLiveActive ?? settings?.is_live_active),
    maxRequestsPerSet:
      settings?.maxRequestsPerSet ?? settings?.max_requests_per_set ?? 50,
    publicSongLanguageMode:
      settings?.publicSongLanguageMode ||
      settings?.public_song_language_mode ||
      "all",

      randomEnabled: Boolean(
      settings?.randomEnabled ??
        settings?.random_enabled ??
        true
    ),
    random_enabled: Boolean(
      settings?.randomEnabled ??
        settings?.random_enabled ??
        true
    ),
    publicTitle: settings?.publicTitle || settings?.public_title || "",
    publicIntro: settings?.publicIntro || settings?.public_intro || "",
    publicTitleEn:
      settings?.publicTitleEn || settings?.public_title_en || "",
    publicIntroEn:
      settings?.publicIntroEn || settings?.public_intro_en || "",
    seoTitle: settings?.seoTitle || settings?.seo_title || "",
    seoDescription:
      settings?.seoDescription || settings?.seo_description || "",
    seoTitleEn: settings?.seoTitleEn || settings?.seo_title_en || "",
    seoDescriptionEn:
      settings?.seoDescriptionEn || settings?.seo_description_en || "",
    faqItems: normalizeFaqItems(settings?.faqItems || settings?.faq_items || []),
    faqItemsEn: normalizeFaqItems(
      settings?.faqItemsEn || settings?.faq_items_en || []
    ),
    heroImageUrl: settings?.heroImageUrl || settings?.hero_image_url || "",
    whatsappNumber: settings?.whatsappNumber || settings?.whatsapp_number || "",
    whatsappMessage:
      settings?.whatsappMessage || settings?.whatsapp_message || "",
    whatsappFloatingEnabled: Boolean(
      settings?.whatsappFloatingEnabled ??
        settings?.whatsapp_floating_enabled ??
        false
    ),
    websiteUrl: settings?.websiteUrl || settings?.website_url || "",
    instagramUrl: settings?.instagramUrl || settings?.instagram_url || "",
    tiktokUrl: settings?.tiktokUrl || settings?.tiktok_url || "",
    youtubeUrl: settings?.youtubeUrl || settings?.youtube_url || "",
    facebookUrl: settings?.facebookUrl || settings?.facebook_url || "",
    spotifyUrl: settings?.spotifyUrl || settings?.spotify_url || "",
    requestSponsors: normalizeRequestSponsors(
      settings?.requestSponsors || settings?.request_sponsors || []
    ),
    requestRaffleEnabled: Boolean(
      settings?.requestRaffleEnabled ??
        settings?.request_raffle_enabled ??
        false
    ),
    request_raffle_enabled: Boolean(
      settings?.requestRaffleEnabled ??
        settings?.request_raffle_enabled ??
        false
    ),
    paidRequestBoostsEnabled: Boolean(
      settings?.paidRequestBoostsEnabled ??
        settings?.paid_request_boosts_enabled ??
        false
    ),
    paid_request_boosts_enabled: Boolean(
      settings?.paidRequestBoostsEnabled ??
        settings?.paid_request_boosts_enabled ??
        false
    ),
    paidRequestBoostAmountCents: Number(
      settings?.paidRequestBoostAmountCents ??
        settings?.paid_request_boost_amount_cents ??
        300
    ),
    paid_request_boost_amount_cents: Number(
      settings?.paidRequestBoostAmountCents ??
        settings?.paid_request_boost_amount_cents ??
        300
    ),
  };
}

function buildWhatsAppUrl(number, message) {
  const cleanNumber = String(number || "").replace(/[^0-9]/g, "");

  if (!cleanNumber) {
    return "";
  }

  const encodedMessage = encodeURIComponent(String(message || "").trim());

  return encodedMessage
    ? `https://wa.me/${cleanNumber}?text=${encodedMessage}`
    : `https://wa.me/${cleanNumber}`;
}

/* wrapper smooth mount */
const pageMotionClass = "opacity-0 animate-[fadeIn_400ms_ease-out_forwards]";

export default function PublicArtistPage({ config, artistSlug }) {
  const { t, i18n } = useTranslation("admin");

  const isEnglish =
    i18n.resolvedLanguage?.toLowerCase().startsWith("en") === true;

  const publicLocale = isEnglish ? "en-GB" : "fi-FI";

  const fallbackArtistName = formatArtistNameFromSlug(
    artistSlug,
    t("publicArtist.demoPerformer", {
      defaultValue: "Demo-esiintyjä",
    })
  );

  const [performer, setPerformer] = useState(null);
  const [publicError, setPublicError] = useState(null);
  const [isLoadingPerformer, setIsLoadingPerformer] = useState(true);
  // PUBLIC_ARTIST_FORCE_DARK_THEME
  // Julkinen esiintyjäsivu käyttää aina Paranoidin tummaa ilmettä.
  // Hallinnan oma teema palautetaan, kun julkiselta sivulta poistutaan.
  useLayoutEffect(() => {
    const root = document.documentElement;

    const previousTheme = root.dataset.theme || "";
    const hadDarkClass = root.classList.contains("theme-dark");
    const hadLightClass = root.classList.contains("theme-light");

    root.dataset.theme = "dark";
    root.classList.remove("theme-light");
    root.classList.add("theme-dark");

    return () => {
      if (previousTheme) {
        root.dataset.theme = previousTheme;
      } else {
        delete root.dataset.theme;
      }

      root.classList.remove("theme-dark", "theme-light");

      if (hadDarkClass) {
        root.classList.add("theme-dark");
      }

      if (hadLightClass) {
        root.classList.add("theme-light");
      }
    };
  }, []);

  const [boostReturnSessionId] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const params = new URLSearchParams(window.location.search);

    return String(params.get("session_id") || "").trim();
  });

  const [boostReturnStatus, setBoostReturnStatus] = useState(() => {
    if (typeof window === "undefined") {
      return "";
    }

    const params = new URLSearchParams(window.location.search);
    const status = params.get("boost");
    const sessionId = String(
      params.get("session_id") || ""
    ).trim();

    if (status === "success") {
      return sessionId ? "checking" : "failed";
    }

    return status === "canceled" ? "canceled" : "";
  });

  useEffect(() => {
    if (!boostReturnStatus || typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);

    url.searchParams.delete("boost");
    url.searchParams.delete("session_id");

    const cleanUrl = `${url.pathname}${url.search}${url.hash}`;

    window.history.replaceState(
      window.history.state,
      "",
      cleanUrl
    );
  }, [boostReturnStatus]);

  /*
   * Stripe-paluu ei vielä yksin tarkoita vahvistettua maksua.
   * Tarkistetaan webhookin tietokantaan tallentama maksutila
   * julkisesta read-only-endpointista.
   */
  useEffect(() => {
    if (
      boostReturnStatus !== "checking" ||
      !boostReturnSessionId ||
      typeof window === "undefined"
    ) {
      return;
    }

    let isCancelled = false;
    let retryTimer = 0;
    let attempt = 0;

    const maxAttempts = 20;

    async function checkBoostPaymentStatus() {
      attempt += 1;

      try {
        const apiBaseUrl =
          window.PARANOIDI_API_BASE_URL ||
          (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
            ? "http://localhost:4000/api"
            : "/backend/api");

        const response = await fetch(
          `${apiBaseUrl}/public/performers/${encodeURIComponent(
            artistSlug
          )}/song-request-boost-status?session_id=${encodeURIComponent(
            boostReturnSessionId
          )}`,
          {
            cache: "no-store",
          }
        );

        const data = await response.json();

        if (isCancelled) {
          return;
        }

        if (response.ok && data?.paid === true) {
          setBoostReturnStatus("active");
          return;
        }

        const paymentStatus = String(
          data?.status || ""
        )
          .trim()
          .toLowerCase();

        if (
          paymentStatus === "failed" ||
          paymentStatus === "expired"
        ) {
          setBoostReturnStatus("failed");
          return;
        }
      } catch (error) {
        console.error(
          "Näkyvyysnoston maksutilan tarkistus epäonnistui:",
          error
        );
      }

      if (!isCancelled && attempt < maxAttempts) {
        retryTimer = window.setTimeout(
          checkBoostPaymentStatus,
          1000
        );
      }
    }

    checkBoostPaymentStatus();

    return () => {
      isCancelled = true;

      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [
    artistSlug,
    boostReturnSessionId,
    boostReturnStatus,
  ]);

  useEffect(() => {
    let isMounted = true;
    let intervalId = 0;

    async function loadPerformer({ showLoader = false } = {}) {
      if (showLoader && isMounted) {
        setIsLoadingPerformer(true);
      }

      try {
        const apiBaseUrl =
          window.PARANOIDI_API_BASE_URL ||
          (window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
            ? "http://localhost:4000/api"
            : "/backend/api");

        const response = await fetch(
          `${apiBaseUrl}/public/performers/${encodeURIComponent(artistSlug)}`,
          {
            cache: "no-store",
          }
        );

        const data = await response.json();

        if (response.ok && data.ok && isMounted) {
          setPublicError(null);
          setPerformer(data.performer);
          return;
        }

        if (isMounted) {
          setPublicError({
            code: data?.code || "PUBLIC_ARTIST_ERROR",
            message:
              data?.error || "Paranoidi-sivua ei voitu avata juuri nyt.",
          });
        }
      } catch (error) {
        console.error("Julkisen esiintyjän haku epäonnistui:", error);
      } finally {
        if (isMounted) {
          setIsLoadingPerformer(false);
        }
      }
    }

    loadPerformer({ showLoader: true });
    intervalId = window.setInterval(loadPerformer, 60000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [artistSlug]);

  const artistName = performer?.name || fallbackArtistName;
  const canShowAdminLink =
  String(config?.performer?.slug || "").toLowerCase() ===
  String(artistSlug || "").toLowerCase();

  const settings = normalizeSettings(performer?.settings || {});
  const heroImageUrl = resolvePublicAssetUrl(settings.heroImageUrl);
  const isLiveActive = settings.isLiveActive;
  const whatsappUrl =
    settings.whatsappFloatingEnabled && settings.whatsappNumber
      ? buildWhatsAppUrl(settings.whatsappNumber, settings.whatsappMessage)
      : "";

  const publicSocialLinks = [
    { label: "Instagram", href: settings.instagramUrl, Icon: FaInstagram },
    { label: "YouTube", href: settings.youtubeUrl, Icon: FaYoutube },
    { label: "TikTok", href: settings.tiktokUrl, Icon: FaTiktok },
    { label: "Facebook", href: settings.facebookUrl, Icon: FaFacebookF },
    { label: "Spotify", href: settings.spotifyUrl, Icon: FaSpotify },
  ].filter((link) => link.href);

  /*
   * Esiintyjän itse kirjoittama sisältö ei ole käyttöliittymätekstiä.
   * Suomenkieliset publicTitle/publicIntro-arvot pidetään FI-näkymässä.
   * EN-näkymä käyttää erillisiä EN-kenttiä, jos ne ovat olemassa.
   * Muuten näytetään lokalisoitu fallback, jotta suomi ei vuoda
   * englanninkieliselle julkiselle sivulle.
   */
  const heroTitle = isEnglish
    ? settings.publicTitleEn ||
      t("publicArtist.hero.fallbackTitle", {
        artistName,
        defaultValue: "{{artistName}} – song request channel",
      })
    : settings.publicTitle ||
      t("publicArtist.hero.fallbackTitle", {
        artistName,
        defaultValue: "Toivo biisiä {{artistName}}",
      });

  const heroIntro = isEnglish
    ? settings.publicIntroEn ||
      t("publicArtist.hero.fallbackIntro", {
        defaultValue:
          "Browse the performer’s repertoire and send a song request for the gig or live show.",
      })
    : settings.publicIntro ||
      t("publicArtist.hero.fallbackIntro", {
        defaultValue:
          "Paranoidi on esiintyjän oma verkkosivu, josta yleisö voi tutustua ohjelmistoon ja lähettää biisitoiveita keikkaa tai liveä varten.",
      });

  const seoTitle = isEnglish
    ? settings.seoTitleEn ||
      t("publicArtist.seo.title", {
        artistName,
        defaultValue: "{{artistName}} – song requests | Paranoidi",
      })
    : settings.seoTitle ||
      settings.publicTitle ||
      t("publicArtist.seo.title", {
        artistName,
        defaultValue: "{{artistName}} – Paranoidi",
      });

  const seoDescription = isEnglish
    ? settings.seoDescriptionEn ||
      t("publicArtist.seo.description", {
        artistName,
        defaultValue:
          "Browse the performer’s repertoire and send a song request when requests are open.",
      })
    : settings.seoDescription ||
      settings.publicIntro ||
      t("publicArtist.seo.description", {
        artistName,
        defaultValue:
          "{{artistName}} käyttää Paranoidia. Tutustu esiintyjään, ohjelmistoon ja lähetä biisitoive, jos toiveet ovat avoinna.",
      });

  /*
   * FAQ-vastaukset ovat esiintyjän omaa sisältöä.
   * EN-sivulla ei näytetä FI-vastauksia. Jos backendissä on faqItemsEn,
   * niitä käytetään automaattisesti.
   */
  const faqItems = isEnglish
    ? settings.faqItemsEn || []
    : settings.faqItems || [];

  useEffect(() => {
    if (!performer) {
      return;
    }

    document.title = seoTitle;

    let descriptionMeta = document.querySelector('meta[name="description"]');

    if (!descriptionMeta) {
      descriptionMeta = document.createElement("meta");
      descriptionMeta.setAttribute("name", "description");
      document.head.appendChild(descriptionMeta);
    }

    descriptionMeta.setAttribute("content", seoDescription);
  }, [performer, seoTitle, seoDescription]);

  const publicConfig = {
    ...config,
    features: performer?.features || config.features || {},
    artistName,
    performerSlug: artistSlug,
    paranoidiSettings: settings,
    settings,
   performer: {
     ...(config.performer || {}),
     ...(performer || {}),
     settings,
   },
    requestSponsors: settings.requestSponsors || [],
    sectionLabels: {
      ...config.sectionLabels,
    },
  };

  function handleAdminNavigation(event) {
    if (
      event?.metaKey ||
      event?.ctrlKey ||
      event?.shiftKey ||
      event?.altKey ||
      event?.button === 1
    ) {
      return;
    }

    event.preventDefault();

    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    const nextPath = "/admin";

    if (currentPath === nextPath) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }

    window.history.pushState({}, "", nextPath);
    window.dispatchEvent(new Event("paranoidi:navigate"));

    try {
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch {
      window.dispatchEvent(new Event("popstate"));
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }

  if (publicError) {
    const isTrialExpired = publicError.code === "TRIAL_EXPIRED";

    return (
      <main className="min-h-screen bg-black text-white">
        <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-black px-4 py-12 text-white sm:px-6">
          <ParanoidiAmbientBackground />

          <div className="relative z-10 w-full max-w-2xl rounded-[2rem] border border-white/10 bg-zinc-950/80 p-8 text-center shadow-2xl shadow-red-950/30 backdrop-blur md:p-10">
            <div className="mx-auto mb-5 inline-flex rounded-full border border-red-400/30 bg-red-500/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-red-200">
              Paranoidi
            </div>

            <h1 className="text-3xl font-black tracking-tight text-white md:text-5xl">
              {isTrialExpired
                ? t("publicArtist.errors.trialExpiredTitle", {
                    defaultValue: "Kokeilujakso on päättynyt",
                  })
                : t("publicArtist.errors.unavailableTitle", {
                    defaultValue: "Paranoidi-sivu ei ole käytössä",
                  })}
            </h1>

            <p className="mx-auto mt-5 max-w-xl text-base leading-7 text-zinc-300 md:text-lg">
              {isTrialExpired
                ? t("publicArtist.errors.trialExpiredText", {
                    defaultValue:
                      "Tämän esiintyjän Paranoidi-sivu ei ole tällä hetkellä käytössä. Esiintyjä voi jatkaa sivun käyttöä aktivoimalla tilauksen hallinnasta.",
                  })
                : isEnglish
                  ? t("publicArtist.errors.unavailableText", {
                      defaultValue:
                        "This Paranoidi page is not available right now.",
                    })
                  : publicError.message ||
                    t("publicArtist.errors.unavailableText", {
                      defaultValue:
                        "Paranoidi-sivua ei voitu avata juuri nyt.",
                    })}
            </p>

            <p className="mt-6 text-sm font-semibold text-zinc-500">
              {artistName}
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black text-white">
      {boostReturnStatus && (
        <div
          role="status"
          className="fixed left-1/2 z-[10000] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 px-1"
           style={{ top: "max(1rem, env(safe-area-inset-top))" }}
        >
          <div
            className={[
              "relative rounded-2xl border px-5 py-4 pr-12 shadow-2xl backdrop-blur-xl",
              boostReturnStatus === "active"
                ? "border-emerald-300/30 bg-emerald-950/95 text-emerald-50"
                : boostReturnStatus === "checking"
                  ? "border-amber-300/30 bg-amber-950/95 text-amber-50"
                  : "border-white/15 bg-zinc-950/95 text-white",
            ].join(" ")}
          >
            <p className="text-sm font-black uppercase tracking-[0.16em]">
              {boostReturnStatus === "active"
                ? t("publicArtist.boost.activeTitle", {
                    defaultValue: "Näkyvyysnosto aktivoitu",
                  })
                : boostReturnStatus === "checking"
                  ? t("publicArtist.boost.checkingTitle", {
                      defaultValue: "Maksu tarkistetaan",
                    })
                  : boostReturnStatus === "failed"
                    ? t("publicArtist.boost.failedTitle", {
                        defaultValue: "Maksu ei vahvistunut",
                      })
                    : t("publicArtist.boost.canceledTitle", {
                        defaultValue: "Maksu peruttu",
                      })}
            </p>

            <p className="mt-1 text-sm font-semibold leading-6 opacity-85">
              {boostReturnStatus === "active"
                ? t("publicArtist.boost.activeText", {
                    defaultValue:
                      "Toiveesi on nyt nostettu tavallisten toiveiden edelle.",
                  })
                : boostReturnStatus === "checking"
                  ? t("publicArtist.boost.checkingText", {
                      defaultValue:
                        "Näkyvyysnosto aktivoituu automaattisesti, kun maksu on vahvistettu.",
                    })
                  : boostReturnStatus === "failed"
                    ? t("publicArtist.boost.failedText", {
                        defaultValue:
                          "Maksua ei vahvistettu. Biisitoiveesi jäi normaalisti voimaan.",
                      })
                    : t("publicArtist.boost.canceledText", {
                        defaultValue:
                          "Maksua ei viimeistelty. Biisitoiveesi jäi normaalisti voimaan.",
                      })}
            </p>

            <button
              type="button"
              aria-label={t("publicArtist.boost.close", {
                defaultValue: "Sulje ilmoitus",
              })}
              onClick={() => setBoostReturnStatus("")}
              className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-black/20 text-lg font-black text-current transition hover:bg-white/10"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-black px-4 py-12 text-white sm:px-6">
        <ParanoidiAmbientBackground />

        {heroImageUrl && (
          <>
            <style>
              {`
               @keyframes paranoidiHeroImageMove {
                  0% {
                    transform: scale(1.04) translate3d(0, 0, 0);
                  }

                  100% {
                    transform: scale(1.22) translate3d(-2.5%, -2%, 0);
                  }
                }
              `}
            </style>

            <img
              src={heroImageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full origin-center object-cover object-[50%_20%] opacity-100 motion-reduce:animate-none"
              style={{
                animation:
                  "paranoidiHeroImageMove 16s ease-in-out infinite alternate"
              }}
            />

            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.62),rgba(0,0,0,0.18),rgba(0,0,0,0.62)),linear-gradient(180deg,rgba(0,0,0,0.08),rgba(0,0,0,0.52))]" />
          </>
        )}

        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.30),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.10),transparent_32%)]" />

        <div
          className={`pointer-events-none absolute inset-0 ${
            heroImageUrl
              ? "bg-gradient-to-b from-black/20 via-black/35 to-black/70"
              : "bg-gradient-to-b from-black/50 via-black/80 to-black"
          }`}
        />

        <style>
          {`
            .paranoidi-public-hero-language > div {
              width: auto !important;
              max-width: none !important;
            }

            .paranoidi-public-hero-language > div > div:first-child,
            .paranoidi-public-hero-language > div > span:first-child {
              display: none !important;
            }
          `}
        </style>

        <div
          className={[
            "paranoidi-public-hero-language absolute z-30 translate-x-0",
            canShowAdminLink
              ? "left-3 top-3 right-auto sm:left-5 sm:top-5"
              : "left-auto right-3 top-3 sm:right-5 sm:top-5",
          ].join(" ")}
        >
          <LanguageSwitcher variant="public" />
        </div>

        <div className="relative mx-auto flex w-full max-w-6xl flex-col items-center text-center [@media(min-height:900px)]:translate-y-12">
          <p className="text-xs font-black uppercase tracking-[0.34em] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.95)]">
            Paranoidi
          </p>

          <h1 className="mt-5 max-w-5xl text-5xl font-black uppercase leading-[0.88] tracking-[-0.075em] text-white sm:text-7xl lg:text-7xl xl:text-8xl">
            {heroTitle}
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-base font-semibold leading-8 text-zinc-300 sm:text-xl sm:leading-9">
            {heroIntro}
          </p>

          {(settings.websiteUrl || publicSocialLinks.length > 0) && (
            <div className="mt-8 flex flex-col items-center gap-5">
              {settings.websiteUrl && (
                <a
                  href={settings.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-full border border-red-300/30 bg-red-500/15 px-6 py-3 text-xs font-black uppercase tracking-[0.22em] text-red-100 no-underline transition hover:border-red-200 hover:bg-red-500/25"
                >
                  {t("publicArtist.hero.website", {
                    defaultValue: "Verkkosivu",
                  })}
                </a>
              )}

              {publicSocialLinks.length > 0 && (
                <div className="text-center">
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {publicSocialLinks.map(({ label, href, Icon }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={label}
                        title={label}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-xl text-zinc-200 no-underline shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:border-red-300/40 hover:bg-red-500/15 hover:text-white"
                      >
                        <Icon aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.04] px-5 py-4 shadow-2xl shadow-black/30 backdrop-blur">
            {isLoadingPerformer ? (
              <p className="text-sm font-black uppercase tracking-[0.18em] text-zinc-400">
                {t("publicArtist.hero.loading", {
                  defaultValue: "Ladataan Paranoidi-sivua...",
                })}
              </p>
       
            ) : isLiveActive ? (
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-red-200">
                  {t("publicArtist.hero.requestsOpen", {
                    defaultValue: "Biisitoiveet ovat auki",
                  })}
                </p>

                {settings.paidRequestBoostsEnabled && (
                  <div className="mx-auto mt-3 max-w-xl border-t border-white/10 pt-3">
                    <p className="text-sm font-semibold leading-6 text-zinc-300">
                      {t("publicArtist.hero.requestIsFree", {
                        defaultValue: "Biisitoiveen lähettäminen on ilmaista.",
                      })}
                    </p>

                   <p className="mt-1 text-sm font-black leading-6 text-white">
                      {t("publicArtist.hero.boostAvailable", {
                        price: (
                          settings.paidRequestBoostAmountCents / 100
                        ).toLocaleString(publicLocale, {
                          maximumFractionDigits: 2,
                        }),
                        defaultValue:
                          "Lähetettyäsi biisitoiveen voit halutessasi nostaa sen {{price}} €:lla tavallisten toiveiden edelle.",
                      })}
                    </p>

                    <p className="mt-1 text-xs font-semibold leading-5 text-zinc-400">
                      {t("publicArtist.hero.boostInfo", {
                        defaultValue:
                          "Jos nostettuja toiveita on useita, ne järjestetään maksujärjestyksessä. Nosto ei takaa kappaleen esittämistä.",
                      })}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-red-200">
                  {t("publicArtist.hero.requestsClosed", {
                    defaultValue: "Biisitoiveet eivät ole juuri nyt auki.",
                  })}
                </p>

                <p className="mt-2 max-w-xl text-sm font-semibold leading-6 text-zinc-400">
                  {t("publicArtist.hero.requestsClosedText", {
                    defaultValue:
                      "Sivu toimii silti esiintyjän julkisena esittelysivuna. Voit tutustua tietoihin ja palata myöhemmin, kun biisitoiveita otetaan vastaan.",
                  })}
                </p>
              </div>
            )}
          </div>

          {isLiveActive && (
            <a
              href="#biisihaku"
              className="mt-5 inline-flex rounded-full bg-red-600 px-7 py-3 text-sm font-black uppercase tracking-wide text-white no-underline shadow-lg shadow-red-950/40 transition hover:bg-red-500 sm:mt-8 sm:px-8 sm:py-4"
            >
              {t("publicArtist.hero.requestButton", {
                defaultValue: "Toivo biisiä",
              })}
            </a>
          )}

        </div>

         {canShowAdminLink && (
          <a
            href="/admin"
            onClick={handleAdminNavigation}
            className="fixed right-3 top-3 z-[9999] rounded-full border border-white/10 bg-black/45 px-3 py-2 text-[0.58rem] font-black uppercase tracking-[0.14em] text-zinc-400 no-underline backdrop-blur-md transition hover:border-red-400/40 hover:bg-red-500/10 hover:text-zinc-100 sm:right-5 sm:top-5 sm:px-4 sm:text-[0.65rem]"
          >
            {t("publicArtist.hero.adminLink", {
              defaultValue: "Esiintyjän hallinta",
            })}
          </a>
        )}

      </section>

      {whatsappUrl && <WhatsAppFloat url={whatsappUrl} />}

      {isLiveActive && <SongFinderSection config={publicConfig} />}

      {faqItems.length > 0 && (
        <PublicFaqSection faqItems={faqItems} />
      )}
    </main>
  );
}

function PublicFaqSection({ faqItems }) {
  const { t } = useTranslation("admin");

  return (
    <section className="bg-[#050706] px-3 py-16 text-white sm:px-6 sm:py-24">
      <div className="mx-auto w-full max-w-7xl">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0b0b0e] p-[1px] shadow-[0_30px_80px_rgba(0,0,0,0.58),0_10px_26px_rgba(0,0,0,0.38)]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
          />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-28 -top-28 h-64 w-64 rounded-full bg-red-600/10 blur-[75px]"
          />

          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-28 left-16 h-52 w-52 rounded-full bg-red-950/25 blur-[70px]"
          />

          <div className="relative rounded-[calc(2rem-1px)] bg-[linear-gradient(145deg,#161619_0%,#0d0d10_55%,#08080a_100%)] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-14px_36px_rgba(0,0,0,0.26)] sm:p-8">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-xs font-black uppercase tracking-[0.28em] text-red-400">
                {t("publicArtist.faq.eyebrow", {
                  defaultValue: "UKK",
                })}
              </p>

              <h2 className="mt-3 text-3xl font-black tracking-[-0.045em] text-white sm:text-5xl">
                {t("publicArtist.faq.title", {
                  defaultValue: "Usein kysytyt kysymykset",
                })}
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-sm font-semibold leading-7 text-zinc-400 sm:text-base">
                {t("publicArtist.faq.intro", {
                  defaultValue:
                    "Vastauksia yleisimpiin kysymyksiin biisitoiveista, ohjelmistosta, keikoista ja yhteydenotosta.",
                })}
              </p>
            </div>

            <div className="mx-auto mt-8 grid max-w-5xl gap-3">
              {faqItems.map((item, index) => (
                <details
                  key={item.id || `${item.question}-${index}`}
                  className="group overflow-hidden rounded-[1.5rem] border border-white/10 bg-[linear-gradient(145deg,#171719_0%,#0d0d10_60%,#09090b_100%)] shadow-[0_16px_36px_rgba(0,0,0,0.32),inset_0_1px_0_rgba(255,255,255,0.06)] transition duration-300 open:border-red-400/30"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-5 text-left sm:px-6">
                    <span className="text-base font-black leading-7 text-white sm:text-lg">
                      {item.question}
                    </span>

                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-[linear-gradient(145deg,#1b1b1f_0%,#101014_100%)] text-red-300 shadow-[0_8px_18px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.06)] transition duration-300 group-open:border-red-400/30 group-open:bg-red-500/10">
                      <svg
                        className="h-4 w-4 transition duration-300 group-open:rotate-180"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </summary>

                  <div className="border-t border-white/[0.06] px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
                    <p className="text-sm font-semibold leading-7 text-zinc-400 sm:text-base">
                      {item.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatsAppFloat({ url }) {
  const { t } = useTranslation("admin");

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      aria-label={t("publicArtist.whatsappAria", {
        defaultValue: "Avaa WhatsApp",
      })}
      style={{
        position: "fixed",
        left: "max(1rem, env(safe-area-inset-left))",
        right: "auto",
        bottom: "max(1rem, env(safe-area-inset-bottom))",
        zIndex: 55,
      }}
      className="inline-flex h-14 w-14 items-center justify-center gap-2 rounded-full border border-emerald-300/40 bg-emerald-500 text-sm font-black text-white no-underline shadow-2xl shadow-black/50 ring-2 ring-white/10 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-emerald-400 sm:h-auto sm:w-auto sm:px-6 sm:py-4"
    >
      <FaWhatsapp
        aria-hidden="true"
        className="text-3xl leading-none sm:text-xl"
      />
      <span className="hidden sm:inline">WhatsApp</span>
    </a>,
    document.body
  );
}